import { useState, useEffect } from "react";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useLocation } from "wouter";
import { Landmark, Building2, BarChart3, RefreshCw, CheckCircle2, Plus, Pencil, Trash2, Settings, Check, ChevronsUpDown, Link2, Unlink } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Lender, InsertLender, Partner } from "@shared/schema";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

const contactFormSchema = z.object({
  userName: z.string().min(1, "Name is required"),
  userEmail: z.string().email("Valid email required"),
  userPhone: z.string().optional(),
  message: z.string().optional(),
});
type ContactFormValues = z.infer<typeof contactFormSchema>;

const lenderFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  company: z.string().min(1, "Company is required"),
  specialty: z.string().min(1, "Specialty is required"),
  description: z.string().min(1, "Description is required"),
  logoUrl: z.string().optional(),
  contactEmail: z.string().email().optional().or(z.literal("")),
  contactPhone: z.string().optional(),
  website: z.string().optional(),
  active: z.boolean().optional(),
  displayOrder: z.coerce.number().int().optional(),
  partnerId: z.number().int().nullable().optional(),
});
type LenderFormValues = z.infer<typeof lenderFormSchema>;

const LOAN_TYPES = [
  {
    icon: BarChart3,
    title: "DSCR Loans",
    badge: "Most Popular",
    badgeColor: "bg-blue-100 text-blue-700",
    description:
      "Debt Service Coverage Ratio (DSCR) loans evaluate your property's rental income rather than your personal income. If the property generates enough cash flow to cover the mortgage payment, you qualify. This makes them ideal for co-living investors with multiple properties or W-2 income that doesn't reflect true investment capacity.",
    bullets: [
      "No personal income verification required",
      "Based on gross long-term rental income vs. mortgage payment",
      "Works great for properties that are outdated and priced right",
      "Available for LLCs and entities",
    ],
  },
  {
    icon: Building2,
    title: "Bank Statement Loans",
    badge: "Flexible",
    badgeColor: "bg-purple-100 text-purple-700",
    description:
      "Bank statement loans use 12–24 months of personal or business bank deposits to verify income — no tax returns needed. If you're self-employed or your income doesn't look strong on paper, this option can bridge the gap and get you into your next co-living property.",
    bullets: [
      "Great for self-employed investors",
      "12 or 24 months of deposits used for qualification",
      "No W-2s or tax returns required",
      "Competitive rates for investment properties",
    ],
  },
  {
    icon: RefreshCw,
    title: "BRRR Strategy",
    badge: "Advanced",
    badgeColor: "bg-green-100 text-green-700",
    description:
      "Buy, Rehab, Rent, Refinance, Repeat (BRRR) is a proven strategy to recycle capital. You acquire a distressed property, renovate to co-living standards, stabilize with tenants, then refinance at the new appraised value — pulling out most or all of your initial investment to redeploy into the next deal.",
    bullets: [
      "Recycles your down payment for the next deal",
      "Works with short-term hard money or bridge loans",
      "Cash-out refinance at 70–75% LTV after stabilization",
      "Pairs well with DSCR loans at refinance stage",
    ],
  },
];

function LenderCard({ lender, onConnect }: { lender: Lender; onConnect: (lender: Lender) => void }) {
  return (
    <Card className="border border-gray-200 hover:shadow-md transition-shadow" data-testid={`lender-card-${lender.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {lender.logoUrl ? (
            <img
              src={lender.logoUrl}
              alt={lender.company}
              className="w-14 h-14 rounded-lg object-contain border border-gray-100 shrink-0 bg-white"
              data-testid={`lender-logo-${lender.id}`}
            />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Landmark className="h-7 w-7 text-blue-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h3 className="font-semibold text-gray-900 text-base" data-testid={`lender-name-${lender.id}`}>{lender.name}</h3>
                <p className="text-sm text-gray-500">{lender.company}</p>
              </div>
              <Badge
                className={cn(
                  "text-xs border-0 shrink-0",
                  lender.kind === "broker" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700",
                )}
                data-testid={`lender-kind-${lender.id}`}
              >
                {lender.kind === "broker" ? "Broker" : "Lender"}
              </Badge>
            </div>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{lender.description}</p>
            {lender.loanTypes && lender.loanTypes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`lender-loan-types-${lender.id}`}>
                {lender.loanTypes.map((lt) => (
                  <Badge key={lt} variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-0" data-testid={`lender-loan-type-${lender.id}-${lt}`}>
                    {lt}
                  </Badge>
                ))}
              </div>
            )}
            {lender.statesServiced && lender.statesServiced.length > 0 && (
              <p className="text-xs text-gray-500 mt-2" data-testid={`lender-states-${lender.id}`}>
                <span className="font-medium text-gray-600">States serviced:</span> {lender.statesServiced.join(", ")}
              </p>
            )}
            <div className="mt-4">
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => onConnect(lender)}
                data-testid={`button-connect-lender-${lender.id}`}
                data-tour="finance-lenders"
              >
                Connect
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LenderCardSkeleton() {
  return (
    <Card className="border border-gray-200">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <Skeleton className="w-14 h-14 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectModal({
  lender,
  open,
  onClose,
}: {
  lender: Lender | null;
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: { userName: "", userEmail: "", userPhone: "", message: "" },
  });

  const mutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      const res = await apiRequest("POST", "/api/app/lenders/connect", {
        lenderId: lender!.id,
        ...values,
      });
      return res.json();
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    },
  });

  const handleClose = () => {
    form.reset();
    setSubmitted(false);
    onClose();
  };

  const onSubmit = (values: ContactFormValues) => {
    mutation.mutate(values);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="modal-connect-lender">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle data-testid="text-modal-title">Connect with {lender?.name}</DialogTitle>
              <DialogDescription>
                Fill out the form below to unlock {lender?.company}'s contact details and get connected.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="userName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Smith" data-testid="input-connect-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="userEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane@example.com" data-testid="input-connect-email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="userPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone (optional)</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="(555) 000-0000" data-testid="input-connect-phone" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message (optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell the lender a bit about your deal or what you're looking for..."
                          rows={3}
                          data-testid="input-connect-message"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 pt-1">
                  <Button type="button" variant="outline" className="flex-1" onClick={handleClose} data-testid="button-cancel-connect">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={mutation.isPending} data-testid="button-submit-connect">
                    {mutation.isPending ? "Submitting..." : "Connect"}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle data-testid="text-modal-success-title">Your message has been sent</DialogTitle>
              <DialogDescription>
                We've delivered your message inside PadPulse. Expect a response within two days.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2 text-green-600 font-medium">
                <CheckCircle2 className="h-5 w-5" />
                <span data-testid="text-success-message">Connection request sent</span>
              </div>
              <p className="text-sm text-gray-600">
                You can track this conversation and continue the discussion anytime from your connections.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate("/app/connections")}
                className="w-full"
                data-testid="button-open-connections"
              >
                Open connections
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminLenderModal({
  open,
  onClose,
  editLender,
}: {
  open: boolean;
  onClose: () => void;
  editLender: Lender | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const form = useForm<LenderFormValues>({
    resolver: zodResolver(lenderFormSchema),
    defaultValues: editLender
      ? {
          name: editLender.name,
          company: editLender.company,
          specialty: editLender.specialty,
          description: editLender.description,
          logoUrl: editLender.logoUrl ?? "",
          contactEmail: editLender.contactEmail ?? "",
          contactPhone: editLender.contactPhone ?? "",
          website: editLender.website ?? "",
          active: editLender.active,
          displayOrder: editLender.displayOrder,
          partnerId: editLender.partnerId ?? null,
        }
      : { name: "", company: "", specialty: "", description: "", logoUrl: "", contactEmail: "", contactPhone: "", website: "", active: true, displayOrder: 0, partnerId: null },
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editLender
          ? {
              name: editLender.name,
              company: editLender.company,
              specialty: editLender.specialty,
              description: editLender.description,
              logoUrl: editLender.logoUrl ?? "",
              contactEmail: editLender.contactEmail ?? "",
              contactPhone: editLender.contactPhone ?? "",
              website: editLender.website ?? "",
              active: editLender.active,
              displayOrder: editLender.displayOrder,
              partnerId: editLender.partnerId ?? null,
            }
          : { name: "", company: "", specialty: "", description: "", logoUrl: "", contactEmail: "", contactPhone: "", website: "", active: true, displayOrder: 0, partnerId: null }
      );
    }
  }, [open, editLender]);

  const [partnerPickerOpen, setPartnerPickerOpen] = useState(false);
  const partnersQuery = useQuery<Partner[]>({
    queryKey: ["/api/admin/partners", "lender"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/partners?partnerType=lender`);
      return res.json();
    },
    enabled: open,
  });
  const partners = partnersQuery.data ?? [];

  const mutation = useMutation({
    mutationFn: async (values: LenderFormValues) => {
      if (editLender) {
        const res = await apiRequest("PATCH", `/api/admin/lenders/${editLender.id}`, values);
        return res.json();
      } else {
        const res = await apiRequest("POST", `/api/admin/lenders`, values);
        return res.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/lenders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/lenders"] });
      toast({ title: editLender ? "Lender updated" : "Lender added" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save lender.", variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="modal-admin-lender">
        <DialogHeader>
          <DialogTitle>{editLender ? "Edit Lender" : "Add Lender"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Name</FormLabel>
                  <FormControl><Input data-testid="input-admin-lender-name" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="company" render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl><Input data-testid="input-admin-lender-company" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="specialty" render={({ field }) => (
              <FormItem>
                <FormLabel>Specialty / Loan Types</FormLabel>
                <FormControl><Input placeholder="e.g. DSCR Loans, Bank Statement" data-testid="input-admin-lender-specialty" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl><Textarea rows={3} data-testid="input-admin-lender-description" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="contactEmail" render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl><Input type="email" data-testid="input-admin-lender-email" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="contactPhone" render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Phone</FormLabel>
                  <FormControl><Input data-testid="input-admin-lender-phone" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="website" render={({ field }) => (
              <FormItem>
                <FormLabel>Website</FormLabel>
                <FormControl><Input placeholder="https://" data-testid="input-admin-lender-website" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="logoUrl" render={({ field }) => (
              <FormItem>
                <FormLabel>Logo URL</FormLabel>
                <FormControl><Input placeholder="https://..." data-testid="input-admin-lender-logo" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="partnerId" render={({ field }) => {
              const selected = partners.find((p) => p.id === field.value);
              return (
                <FormItem className="flex flex-col">
                  <FormLabel>Linked Partner Account</FormLabel>
                  <Popover open={partnerPickerOpen} onOpenChange={setPartnerPickerOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          className={cn("justify-between font-normal", !selected && "text-muted-foreground")}
                          data-testid="button-admin-lender-partner"
                        >
                          {selected ? `${selected.companyName} — ${selected.contactName}` : "No linked partner"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search partners..." data-testid="input-search-lender-partner" />
                        <CommandList>
                          <CommandEmpty>No partners found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="__none__"
                              onSelect={() => { field.onChange(null); setPartnerPickerOpen(false); }}
                              data-testid="option-lender-partner-none"
                            >
                              <Check className={cn("mr-2 h-4 w-4", field.value == null ? "opacity-100" : "opacity-0")} />
                              No linked partner
                            </CommandItem>
                            {partners.map((p) => (
                              <CommandItem
                                key={p.id}
                                value={`${p.companyName} ${p.contactName} ${p.email}`}
                                onSelect={() => { field.onChange(p.id); setPartnerPickerOpen(false); }}
                                data-testid={`option-lender-partner-${p.id}`}
                              >
                                <Check className={cn("mr-2 h-4 w-4", field.value === p.id ? "opacity-100" : "opacity-0")} />
                                <span className="truncate">{p.companyName} — {p.contactName} <span className="text-muted-foreground">({p.email})</span></span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">Messages from hosts who connect with this lender will route to the linked partner's portal inbox.</p>
                  <FormMessage />
                </FormItem>
              );
            }} />
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="displayOrder" render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Order</FormLabel>
                  <FormControl><Input type="number" data-testid="input-admin-lender-order" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="active" render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Active</FormLabel>
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value ?? true}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 mt-2"
                      data-testid="input-admin-lender-active"
                    />
                  </FormControl>
                </FormItem>
              )} />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={mutation.isPending} data-testid="button-save-lender">
                {mutation.isPending ? "Saving..." : editLender ? "Save Changes" : "Add Lender"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function AdminLenderSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit("financials");
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editLender, setEditLender] = useState<Lender | null>(null);

  const { data: adminLenders, isLoading } = useQuery<Lender[]>({
    queryKey: ["/api/admin/lenders"],
  });

  const { data: lenderPartners } = useQuery<Partner[]>({
    queryKey: ["/api/admin/partners", "lender"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/partners?partnerType=lender`);
      return res.json();
    },
  });
  const partnerById = new Map((lenderPartners ?? []).map((p) => [p.id, p]));

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      await apiRequest("DELETE", `/api/admin/lenders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/lenders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/lenders"] });
      toast({ title: "Lender removed" });
    },
    onError: () => {
      toast({ title: "Error deleting lender", variant: "destructive" });
    },
  });

  return (
    <div className="mt-8 border-t border-gray-200 pt-8" data-testid="section-admin-lenders">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-gray-400" />
          <h2 className="text-base font-semibold text-gray-700">Manage Lenders</h2>
        </div>
        <Button size="sm" variant="outline" disabled={readOnly} onClick={() => { if (readOnly) return; setEditLender(null); setShowAdminForm(true); }} data-testid="button-add-lender">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Lender
        </Button>
      </div>
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
        </div>
      ) : adminLenders && adminLenders.length > 0 ? (
        <div className="space-y-2">
          {adminLenders.map((l) => (
            <div key={l.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-4 py-2.5 bg-white" data-testid={`admin-lender-row-${l.id}`}>
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${l.active ? "bg-green-400" : "bg-gray-300"}`} />
                <div>
                  <p className="font-medium text-sm text-gray-800">{l.name} — {l.company}</p>
                  <p className="text-xs text-gray-400">{l.specialty}</p>
                  {l.partnerId != null ? (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs text-brand" data-testid={`text-lender-linked-${l.id}`}>
                      <Link2 className="h-3 w-3" />
                      Linked: {partnerById.get(l.partnerId)?.companyName ?? `Partner #${l.partnerId}`}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs text-gray-400" data-testid={`text-lender-unlinked-${l.id}`}>
                      <Unlink className="h-3 w-3" />
                      Not linked
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={readOnly} onClick={() => { if (readOnly) return; setEditLender(l); setShowAdminForm(true); }} data-testid={`button-edit-lender-${l.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => { if (readOnly) return; deleteMutation.mutate(l.id); }} disabled={readOnly || deleteMutation.isPending} data-testid={`button-delete-lender-${l.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400">No lenders yet. Add one above.</p>
      )}
      <AdminLenderModal
        open={showAdminForm}
        onClose={() => setShowAdminForm(false)}
        editLender={editLender}
      />
    </div>
  );
}

export default function AppFinance() {
  usePageTitle("Finance");
  const [connectLender, setConnectLender] = useState<Lender | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const { data: lenders, isLoading } = useQuery<Lender[]>({
    queryKey: ["/api/app/lenders"],
  });

  const { data: adminCheck } = useQuery<{ authenticated: boolean }>({
    queryKey: ["/api/admin/check"],
  });
  const isAdmin = adminCheck?.authenticated === true;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="financials" />
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Landmark className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="text-finance-heading" data-tour="finance-heading">Finance</h1>
                <p className="text-sm text-gray-500">Financing strategies and recommended lenders for co-living investors</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setShowAdmin((v) => !v)}
                  data-testid="button-toggle-admin"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              <div className="flex items-center gap-2">
                <ExplainerVideoButton pageKey="finance" />
                <GuideButton pageKey="finance" />
              </div>
            </div>
          </div>
        </div>
        <GuideNudgeBanner pageKey="finance" />

        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-1" data-testid="text-education-heading">Co-Living Financing 101</h2>
          <p className="text-sm text-gray-500 mb-6">
            Traditional loans measure your income — co-living financing measures your property's income. Here are the three most viable options for building a co-living portfolio, and why choosing the right one matters.
          </p>
          <div className="grid gap-5 md:grid-cols-3" data-tour="finance-add">
            {LOAN_TYPES.map((lt) => {
              const Icon = lt.icon;
              return (
                <Card key={lt.title} className="border border-gray-200 hover:shadow-sm transition-shadow" data-testid={`card-loan-type-${lt.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <Badge className={`text-xs border-0 ${lt.badgeColor}`}>{lt.badge}</Badge>
                    </div>
                    <CardTitle className="text-base mt-2">{lt.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3 leading-relaxed">{lt.description}</p>
                    <ul className="space-y-1.5">
                      {lt.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-xs text-gray-500">
                          <CheckCircle2 className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="mt-5 p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800 leading-relaxed">
            <strong>All three strategies are proven for co-living portfolios</strong> — the right choice depends on your current income documentation, deal type, and exit strategy. Our recommended lenders below specialize in these exact loan products.
          </div>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-relaxed" data-testid="section-ia-strategy" data-tour="finance-loans">
            <strong>Want to learn how to end up with no money in the deal?</strong> Connect with our recommended lenders below and ask about the <strong>IA Strategy</strong> — a financing approach designed to help you potentially recover up to your entire initial down payment investment while keeping the property cash-flowing.
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1" data-testid="text-lenders-heading" data-tour="finance-lenders">Recommended Lenders &amp; Brokers</h2>
          <p className="text-sm text-gray-500 mb-6">
            Click "Connect" on any lender or broker to unlock their contact information. We track referral volume to negotiate better rates for our community over time.
          </p>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => <LenderCardSkeleton key={i} />)}
            </div>
          ) : lenders && lenders.length > 0 ? (
            <div className="space-y-4">
              {lenders.map((l) => (
                <LenderCard key={l.id} lender={l} onConnect={setConnectLender} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-xl" data-testid="text-no-lenders">
              <Landmark className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No lenders yet</p>
              <p className="text-sm">Check back soon — we're vetting our first recommended partners.</p>
            </div>
          )}
        </div>

        {isAdmin && showAdmin && <AdminLenderSection />}

        <ConnectModal
          lender={connectLender}
          open={!!connectLender}
          onClose={() => setConnectLender(null)}
        />
      </div>
    </AppLayout>
  );
}
